<template>
  <div class="min-h-screen-safe bg-slate-900 font-sans text-slate-200 flex flex-col">
    
    <header class="flex-none sticky top-0 z-50 bg-[#0B1021]/95 backdrop-blur-md border-b border-slate-800 pt-safe flex flex-col shadow-lg">
      <div class="px-3 py-3 flex items-center justify-between relative">
        <NuxtLink :to="localePath('/')" class="flex items-center gap-1 text-slate-400 hover:text-white transition-colors group p-1 z-10">
          <svg class="w-6 h-6 transform group-hover:-translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
          <span class="text-[14px] font-bold tracking-wider">{{ locale === 'zh' ? '返回' : 'Back' }}</span>
        </NuxtLink>
        <h1 class="absolute left-1/2 -translate-x-1/2 font-bold text-[15px] sm:text-[16px] text-white tracking-widest uppercase pointer-events-none w-max">
          {{ locale === 'zh' ? '常见问题 FAQ' : 'FAQ' }}
        </h1>
        <div class="w-16"></div>
      </div>
    </header>

    <main class="flex-auto max-w-3xl mx-auto w-full p-5 pb-safe">
      <FaqZh v-if="locale === 'zh'" />
      <FaqEn v-else />
      <div class="h-12"></div>
    </main>

  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import FaqZh from '~/components/faq/FaqZh.vue'
import FaqEn from '~/components/faq/FaqEn.vue'

const { locale } = useI18n()

// 🚀 引入路径智能转换引擎
const localePath = useLocalePath()

// ==========================================
// 🚀 核弹级 AEO 武器：动态双语 JSON-LD 结构化数据
// ==========================================
const faqSchema = computed(() => {
  if (locale.value === 'zh') {
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "马来西亚和新加坡万字 (4D) 的开彩时间是几点？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "大马彩、万能、多多、砂拉越、山打根、沙巴为晚上 7:00。新加坡万字为晚上 6:30（除每月第一个周三需等大彩结束）。豪龙 (GD Lotto) 晚上 7:08，9Lotto 晚上 7:30。"
          }
        },
        {
          "@type": "Question",
          "name": "马来西亚 4D 万字通常在星期几开彩？什么是特别开彩（Special Draw）？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "马来西亚的正常开彩日为每星期的三、六和日。而特别开彩（Special Draw）一定是在星期二，具体日期由官方财政部公布。如果您想获取最准确的特别开彩日期表，您可以随时登录 Lottobox.cc，询问我们的 24 小时 AI 客服！豪龙和9Lotto则是每日开奖。"
          }
        },
        {
          "@type": "Question",
          "name": "为什么每个月第一个星期三新加坡马会 (Singapore Pools) 的成绩会延迟公布？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "因为每月的第一个星期三是新加坡的大彩日（Big Sweep）。官方需要先完成大彩的摇珠流程，因此 4D 万字票成绩通常会延迟到晚上 7:20 左右才会公布。"
          }
        },
        {
          "@type": "Question",
          "name": "什么是万字 4D 开奖的晋级制度？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "豪龙、9Lotto、万能、多多和山打根采用晋级制度。官方会先抽出 13 个特别奖 (Special) 号码，最后再从这 13 个号码中抽出 3 个，直接晋级成为当期的头奖、二奖和三奖。"
          }
        },
        {
          "@type": "Question",
          "name": "Lottobox 的马票和万字成绩准确吗？数据从哪里来？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "极其准确。Lottobox 采用最新的直连 API 和官方现场摇珠直播（Broadcast live draw）同步技术，确保赛果与官方毫无延迟，为您提供最权威的开奖数据。"
          }
        },
        {
          "@type": "Question",
          "name": "我可以在 Lottobox 线上购买万字或买马票吗？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "不可以。Lottobox 是一个 100% 免费、独立的第三方数据聚合和现场直播平台。我们严格禁止且不提供任何形式的在线投注、买字或赌博服务。"
          }
        }
      ]
    }
  } else {
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What are the 4D draw times in Malaysia and Singapore?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Magnum, TOTO, Da Ma Cai, Sarawak, Sandakan, Sabah at 7:00 PM. Singapore Pools at 6:30 PM. GD Lotto at 7:08 PM and 9Lotto at 7:30 PM daily."
          }
        },
        {
          "@type": "Question",
          "name": "What days are the 4D draws in Malaysia? What is a Special Draw?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Standard draw days in Malaysia are Wednesday, Saturday, and Sunday. A Special Draw is always held on a Tuesday, with specific dates announced by the Ministry of Finance. For the most accurate Special Draw dates, you can log in to Lottobox.cc and ask our 24/7 AI Customer Service! GD Lotto and 9Lotto draw daily."
          }
        },
        {
          "@type": "Question",
          "name": "Why are Singapore Pools 4D results delayed on the first Wednesday of the month?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The first Wednesday of the month is the Big Sweep draw day in Singapore. As the Big Sweep draw takes precedence, 4D results are usually delayed until around 7:20 PM."
          }
        },
        {
          "@type": "Question",
          "name": "How does the 4D Promotion System work?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "For GD Lotto, Magnum, TOTO, and Sandakan: 13 Special numbers are drawn first. Then, 3 of these 13 numbers are promoted to the 1st, 2nd, and 3rd Prizes."
          }
        },
        {
          "@type": "Question",
          "name": "Are the 4D results on Lottobox accurate? Where does the data come from?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, highly accurate. Lottobox uses direct API synchronization and tracks official Broadcast live draws to ensure our results are updated in real-time, matching the official announcements."
          }
        },
        {
          "@type": "Question",
          "name": "Can I buy 4D tickets or place bets online through Lottobox?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No. Lottobox is an independent, 100% free data aggregator and live stream platform. We strictly prohibit and do not offer any online betting or gambling services."
          }
        }
      ]
    }
  }
})

// 🚀 页面 Meta 标签也同步升华
useHead(() => ({
  title: locale.value === 'zh' 
    ? '常见问题 FAQ - 4D 万字与马票开奖时间、特别开彩规则 | Lottobox Live' 
    : 'FAQ - 4D Draw Times, Special Draws & Rules | Lottobox Live',
  meta: [
    { 
      name: 'description', 
      content: locale.value === 'zh' 
        ? '完整解答马来西亚与新加坡 4D 万字、马票开奖时间、特别开彩日期、大彩延迟原因及常见问题。全网最快 Lottobox 查马票指南。'
        : 'Complete FAQ for 4D draw times, Special Draw dates, Singapore Pools delays, and rules for Magnum, TOTO, and GD Lotto on Lottobox.' 
    }
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: () => JSON.stringify(faqSchema.value)
    }
  ]
}))
</script>
